import type { UsdCadSeries } from "./data/fx-loader";
import type { PrimeRateSeries } from "./data/rates-loader";
import type { Cagr, NominalMonthly } from "./rates";

/* ──────────────────────────────────────────────────────────────────────────
 * Simulator primitive — types.
 *
 * The simulator is built around a pure function `runSimulation(scenario)`
 * that produces a `SimResult`. This file defines the inputs and outputs.
 *
 * The Scenario carries:
 *   - A loan side, which is a discriminated union:
 *       `{ kind: "flat",       rate }`         — constant rate (synthetic / unit-test scenarios)
 *       `{ kind: "historical", spread, primeSeries }` — Prime + spread, rate varies over time
 *   - A stock side, which is also a discriminated union:
 *       `{ kind: "synthetic", cagr, months }`           — smooth growth, math-engine testing.
 *       `{ kind: "real",      ticker, series, divs }`   — real historical prices, ~daily.
 *
 * Real-data simulations use a daily loop (one row per trading day) with
 * interest still accruing at month-end boundaries (HELOC nominal-monthly
 * convention preserved). Synthetic simulations use a monthly loop.
 *
 * Tax math is a separate layer that consumes `SimResult` — see `MATH.md`.
 * The simulator records financial facts only.
 * ────────────────────────────────────────────────────────────────────────── */

// ── Price data ──────────────────────────────────────────────────────────────

/** One day of price data — date as ISO YYYY-MM-DD, close as the raw close
 *  (split-adjusted upstream by the data source). */
export type DailyPrice = {
  date: string;
  close: number;
};

/** A sequence of daily prices, monotonically ordered by date. */
export type PriceSeries = readonly DailyPrice[];

/** A single cash-dividend event — ex-dividend date and $/unit paid. */
export type DividendEvent = {
  readonly date: string;
  readonly amount: number;
};

/**
 * How the simulator handles dividend events.
 *
 *   - `"drip"`: on each ex-div day, units held × $/unit = cash. Immediately
 *     buy more units at that day's close. Units grow over time.
 *   - `"ignore"`: dividends are not modelled. The simulator under-states
 *     real total return by whatever the dividend yield was. (This is the
 *     Slice 4 behaviour, kept as the default so existing scenarios that
 *     don't pass dividends behave identically.)
 *
 * Future modes (own slice when configurability lands): `"cash-to-loan"`
 * (Smith Manoeuvre's signature — pay HELOC interest from dividends),
 * `"cash-to-account"` (sit idle as cash).
 */
export type DividendMode = "drip" | "ignore";

// ── Stock source (discriminated union) ──────────────────────────────────────

/**
 * Where the stock-side growth comes from. Two flavours:
 *
 *   - `synthetic`: smooth CAGR-based growth. Useful for testing the math
 *     engine against closed-form expressions (every monthly step is exactly
 *     `value × (1 + cagr)^(1/12)`).
 *
 *   - `real`: a real historical price series. The portfolio holds `units`
 *     of the instrument bought at the first price; subsequent values are
 *     `units × close[t]`. Optionally carries dividend events and a mode
 *     telling the simulator how to handle them.
 */
export type StockSource =
  | { readonly kind: "synthetic"; readonly cagr: Cagr; readonly months: number }
  | {
      readonly kind: "real";
      readonly ticker: string;
      /** Prices in the ticker's native quote currency. CAD by default. */
      readonly series: PriceSeries;
      /** Dividend events in the ticker's native quote currency. */
      readonly dividends: readonly DividendEvent[];
      readonly dividendMode: DividendMode;
      /**
       * When present, the price series is interpreted as USD and the
       * simulator converts each step's portfolio value to CAD using the
       * USD/CAD rate at that date. When absent (the default), the price
       * series is interpreted as CAD with no conversion.
       *
       * The caller is responsible for ensuring `fxSeries` covers the
       * entire date range of `series` — clamp `series` to the FX
       * coverage window before constructing the scenario.
       */
      readonly fxSeries?: UsdCadSeries;
    };

/** Convenience constructor for synthetic stock sources. */
export const syntheticStock = (cagr: Cagr, months: number): StockSource => ({
  kind: "synthetic",
  cagr,
  months,
});

/** Convenience constructor for real-data stock sources.
 *
 *  Dividend handling is opt-in: if you don't pass `dividends` + `dividendMode`,
 *  the simulator ignores dividends (Slice 4 behaviour, preserved for backward
 *  compatibility with the canonical scenarios). To enable DRIP, pass both
 *  the dividend events and `dividendMode: "drip"`. */
export const realStock = (
  ticker: string,
  series: PriceSeries,
  options: {
    dividends?: readonly DividendEvent[];
    dividendMode?: DividendMode;
    /** If provided, the price series is interpreted as USD and converted
     *  to CAD at each step using the USD/CAD rate at that date. */
    fxSeries?: UsdCadSeries;
  } = {},
): StockSource => ({
  kind: "real",
  ticker,
  series,
  dividends: options.dividends ?? [],
  dividendMode: options.dividendMode ?? "ignore",
  ...(options.fxSeries ? { fxSeries: options.fxSeries } : {}),
});

// ── Loan source (discriminated union) ──────────────────────────────────────

/**
 * Where the loan-side rate comes from. Two flavours:
 *
 *   - `flat`: a constant nominal-monthly rate, supplied by an instrument
 *     constructor from `rates.ts` (`helocRate`, `plocRate`, etc.). Used by
 *     synthetic scenarios and any test that wants closed-form math.
 *
 *   - `historical`: a base series (currently Canadian Prime from BoC) plus
 *     a constant spread on top. The effective rate varies over time as the
 *     base series moves. Used by the live `/simulate` page so the loan
 *     reflects actual historical rate cycles.
 *
 * `spread` is a decimal annual rate (e.g., 0.005 for 50 bps / Prime + 0.50%).
 * The monthly rate at any date is `(primeAt(date) + spread) / 12`, matching
 * the HELOC nominal-monthly convention used everywhere else.
 */
export type LoanSource =
  | { readonly kind: "flat"; readonly rate: NominalMonthly }
  | { readonly kind: "historical"; readonly spread: number; readonly primeSeries: PrimeRateSeries };

/** Convenience constructor for flat-rate loans. */
export const flatLoan = (rate: NominalMonthly): LoanSource => ({
  kind: "flat",
  rate,
});

/** Convenience constructor for historical-rate loans (Prime + spread). */
export const historicalLoan = (spread: number, primeSeries: PrimeRateSeries): LoanSource => ({
  kind: "historical",
  spread,
  primeSeries,
});

// ── Scenario + output ───────────────────────────────────────────────────────

export type Scenario = {
  /** Initial loan principal, in dollars. */
  borrowed: number;
  /** Where the loan-side rate comes from — see LoanSource. */
  loanRate: LoanSource;
  /** Where the stock-side comes from — see StockSource. */
  stock: StockSource;
  /** When true, accrued interest is added to the loan balance each month
   *  (capitalized — Smith Manoeuvre / readvanceable HELOC behaviour). */
  capitalizeInterest: boolean;
};

/** One row of the simulation output. For synthetic scenarios, each step is
 *  one month. For real-data scenarios, each step is one trading day. */
export type SimStep = {
  /** Step index. 0 = initial state. */
  step: number;
  /** ISO date for this step (real-data only; for synthetic, this is the
   *  symbolic "month N" formatted as a phantom date for chart axis consistency). */
  date: string;
  portfolioValue: number;
  loanBalance: number;
  equity: number;
  /** Interest accrued during this step. Non-zero on month-end-crossing
   *  steps for real-data; non-zero every step for synthetic. */
  interestAccruedThisMonth: number;
  /** Cash equivalent of any dividend event landing on this step (units × $/unit
   *  at the moment of the ex-dividend day). Non-zero only on ex-dividend dates
   *  for real-data scenarios with dividend events; always 0 for synthetic and
   *  for `dividendMode: "ignore"`. Even with DRIP enabled the simulator
   *  reinvests this amount into units behind the scenes, but the cash value is
   *  retained here so per-year breakdowns and downstream UIs can surface the
   *  income stream without re-deriving it. */
  cashDividendThisStep: number;
};

export type SimResult = {
  scenario: Scenario;
  /** Length depends on scenario.stock: months + 1 for synthetic; series.length for real. */
  series: SimStep[];
  /** Convenience accessor for the last step in `series`. */
  final: SimStep;
};
